import { useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid2 as Grid, Alert, FormControlLabel, Switch, CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { customersApi, type CustomerDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  customer?: CustomerDto | null;
}

interface FormData {
  code: string;
  name: string;
  phone?: string;
  email?: string;
  uhid?: string;
  creditLimit?: number | null;
  isActive: boolean;
  gstin?: string;
  taxRegistrationNumber?: string;
}

const empty: FormData = {
  code: "", name: "", phone: "", email: "", uhid: "",
  creditLimit: null, isActive: true,
  gstin: "", taxRegistrationNumber: "",
};

export function CustomerForm({ open, onClose, customer }: CustomerFormProps) {
  const qc = useQueryClient();
  const isEdit = !!customer;
  const { taxIdLabel, isGst, isVat, isNone } = useTaxRegime();
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<FormData>({ defaultValues: empty });

  useEffect(() => {
    reset(customer ? {
      code: customer.code,
      name: customer.name,
      phone: customer.phone ?? "",
      email: customer.email ?? "",
      uhid: customer.uhid ?? "",
      creditLimit: customer.creditLimit ?? null,
      isActive: customer.isActive,
      gstin: customer.gstin ?? "",
      taxRegistrationNumber: customer.taxRegistrationNumber ?? "",
    } : empty);
  }, [customer, open, reset]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Customer updated" : "Customer created" },
    mutationFn: async (data: FormData) => {
      // Only send the tax id for the active regime; clear the other column so
      // a switch from VAT → GST doesn't leave stale identifier data behind.
      const payload = {
        ...data,
        phone: data.phone || undefined,
        email: data.email || undefined,
        uhid: data.uhid || undefined,
        creditLimit: data.creditLimit ?? undefined,
        gstin: isGst ? (data.gstin || undefined) : undefined,
        taxRegistrationNumber: isVat ? (data.taxRegistrationNumber || undefined) : undefined,
      };
      if (isEdit && customer) {
        await customersApi.update(customer.id, payload);
        return customer.id;
      }
      const result = await customersApi.create(payload);
      return result.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <DialogTitle>{isEdit ? "Edit customer" : "New customer"}</DialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="code" control={control}
                rules={{ required: "Required", maxLength: 50 }}
                render={({ field }) => (
                  <TextField {...field} label="Code *" fullWidth size="small" disabled={isEdit}
                    error={!!errors.code} helperText={errors.code?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller
                name="name" control={control}
                rules={{ required: "Required", maxLength: 200 }}
                render={({ field }) => (
                  <TextField {...field} label="Name *" fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="phone" control={control}
                render={({ field }) => <TextField {...field} label="Phone" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="email" control={control}
                rules={{ pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email" } }}
                render={({ field }) => (
                  <TextField {...field} type="email" label="Email" fullWidth size="small"
                    error={!!errors.email} helperText={errors.email?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="uhid" control={control}
                render={({ field }) => (
                  <TextField {...field} label="UHID" fullWidth size="small"
                    helperText="Unique Hospital ID (optional)" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="creditLimit" control={control}
                rules={{ min: { value: 0, message: "Must be >= 0" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Credit limit (₹)" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                    value={field.value ?? ""}
                    error={!!errors.creditLimit} helperText={errors.creditLimit?.message} />
                )}
              />
            </Grid>
            {!isNone && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Controller
                  name={isVat ? "taxRegistrationNumber" : "gstin"}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label={taxIdLabel} fullWidth size="small"
                      value={field.value ?? ""}
                      inputProps={{ style: { textTransform: "uppercase" } }}
                      helperText={isVat ? "VAT registration / TRN" : "15-char India GSTIN"} />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="isActive" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Active"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained"
            disabled={isSubmitting || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
